import { useForm } from "@website/form/hook/form.hooks";
import { MaterialIcon } from "@website/icons/material";
import {
  Button,
  ButtonColorType,
  ButtonSizeType,
  ButtonVariantType
} from "@website/ui/button";
import { LazyImage } from "@website/ui/lazy-image";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import {
  CheckboxFilter,
  CounterFilter,
  Input,
  RadioFilter,
  RangeFilter,
  SwitchFilter,
  TimeFilter
} from "@website/widget/filter/filter-components";
import { MultiCheckbox } from "@website/widget/filter/filter-components/multi-checkbox";
import { VisaFilter } from "@website/widget/filter/filter-components/visa";
import {
  useCityFilterState,
  useFilterLayoutEffect,
  useFilterTracking,
  useFilters
} from "@website/widget/filter/filter.hook";
import type {
  FilterResultType,
  FiltersSchemaType,
  MapFilterProperties
} from "@website/widget/filter/filter.types";
import { getQuery } from "@website/widget/filter/helpers";
import styles from "@website/widget/filter/index.module.scss";
import isEmpty from "lodash-es/isEmpty";
import { useRouter } from "next/router";
import {
  Children,
  FormEvent,
  Fragment,
  memo,
  useCallback,
  useEffect
} from "react";
import { FormProvider, Mode } from "react-hook-form";
import { ObjectSchema } from "yup";
import IranMapImage from "../../assets/images/iran_map.png";

interface ResetButtonProperties {
  size: ButtonSizeType;
  color: ButtonColorType;
  variant: ButtonVariantType;
}

interface FilterProperties<T extends Record<string, unknown>> {
  onChange?(value: FilterResultType): void;
  validations?: ObjectSchema<T>;
  mode?: Mode;
  schema: FiltersSchemaType;
  title: string;
  loading?: boolean;
  handleFiltersAction(): void;
  filtersActionTitle: string;
  resetButtonProps: ResetButtonProperties;
  onResetFilters?(): void;
  className?: string;
  filtersClassName?: string;
  mapFilterProps?: MapFilterProperties;
  scroll?: boolean;
  trackingCallback?({ removed }: { removed: object }): void;
  key?: string;
}

const Filters = <T extends Record<string, unknown>>(
  properties: FilterProperties<T>
) => {
  const {
    schema,
    loading,
    title,
    onChange,
    validations,
    mode,
    handleFiltersAction,
    filtersActionTitle,
    resetButtonProps,
    onResetFilters,
    className,
    filtersClassName,
    mapFilterProps,
    scroll,
    trackingCallback,
    key
  } = properties;

  const filtersSchemaEntries = Object.entries(schema);

  const router = useRouter();

  const methods = useForm<T>({
    mode,
    validations
  });

  const { isMobile, formRef } = useFilterLayoutEffect();

  const { setFilterQueries, selectedFiltersCount } = useFilters({
    schema,
    scroll
  });

  const { onCloseFilters, visible } = useCityFilterState();

  useFilterTracking(trackingCallback ?? (() => {}), {
    schema,
    trackFilters: Boolean(trackingCallback)
  });

  useEffect(() => {
    const watcher = methods.watch((values, object) => {
      const [name] = object.name?.split(".") ?? [];
      const schema = (values as T)[name] as {
        type: string;
        value: unknown;
        withDebounce?: boolean;
      };

      const parameters = {
        name,
        asQuery: getQuery(schema),
        withDebounce: schema?.withDebounce
      };
      setFilterQueries(parameters)?.then(() => onChange?.(parameters));
    });

    return watcher.unsubscribe;
  }, [methods, onChange, setFilterQueries]);

  const children = filtersSchemaEntries
    .map(([name, schema]) => {
      switch (schema.type) {
        case "range": {
          return <RangeFilter key={name} {...schema} />;
        }
        case "time-range": {
          return <TimeFilter key={name} {...schema} />;
        }
        case "input": {
          return <Input key={name} {...schema} />;
        }
        case "counter": {
          return <CounterFilter key={name} name={name} {...schema} />;
        }
        case "switch": {
          return <SwitchFilter key={name} name={name} {...schema} />;
        }
        case "radio": {
          return <RadioFilter key={name} name={name} {...schema} />;
        }
        case "checkbox": {
          return <CheckboxFilter key={name} name={name} {...schema} />;
        }
        case "multi-checkbox": {
          return <MultiCheckbox key={name} name={name} {...schema} />;
        }
        case "visa": {
          return <VisaFilter key={name} name={name} {...schema} />;
        }
        default: {
          return null;
        }
      }
    })
    .filter(Boolean);

  const onReset = useCallback(() => {
    methods.reset();
    formRef.current?.reset();
    onResetFilters?.();
  }, [formRef, methods, onResetFilters]);

  const onFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  }, []);
  return (
    <aside
      className={`${styles["panel-container"]} ${
        visible ? styles["visible"] : ""
      } ${className ?? ""}`}
      role={visible && isMobile ? "dialog" : undefined}
      key={key}
    >
      {isEmpty(mapFilterProps) ? null : (
        <div
          className={styles["filter-map-container"]}
          onClick={mapFilterProps.onClick}
        >
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <MaterialIcon
                className="text-2xl"
                name="map"
                variant="outlined"
              />
              <span className="font-medium">{mapFilterProps.label}</span>
            </div>
            <MaterialIcon
              className="text-secondary text-2xl"
              name="chevron_left"
              variant="fill"
            />
          </div>

          <div className="h-[104px] w-full overflow-hidden rounded-lg">
            <LazyImage
              alt="iran_map"
              className="size-full object-cover object-center"
              src={IranMapImage.src}
            />
          </div>
        </div>
      )}
      <div className={`${styles["filters"]} ${filtersClassName ?? ""}`}>
        <div className={styles["filters-header-box"]}>
          <div className={styles["filters-title-container"]}>
            <Button
              className={styles["close-button"]}
              color="neutral"
              onClick={onCloseFilters}
              variant="text"
            >
              <MaterialIcon
                className="text-on-surface-medium-emphasis text-2xl"
                name="arrow_forward"
              />
            </Button>
            <span className={styles["filters-header-title"]}>{title}</span>
          </div>
          <Button
            {...resetButtonProps}
            StartIcon={
              Number(selectedFiltersCount) ? (
                <MaterialIcon name="delete_outline" />
              ) : null
            }
            className="h-16"
            hidden={!selectedFiltersCount}
            onClick={onReset}
          >
            {selectedFiltersCount
              ? `حذف فیلترها (${numberConvert(selectedFiltersCount, {
                  locale: router.locale
                })})`
              : "حذف فیلترها"}
          </Button>
        </div>
        <form
          className={styles["filters-form-container"]}
          onSubmit={onFormSubmit}
          ref={formRef}
        >
          <FormProvider {...methods}>
            {Children.map(children, (child, index) => (
              <Fragment key={child?.key ?? index}>
                {child}
                {Children.count(children) - 1 !== index && (
                  <div className={styles["divider"]} />
                )}
              </Fragment>
            ))}
          </FormProvider>
        </form>
        <div className={styles["filters-footer-container"]}>
          <Button
            color="secondary"
            fullWidth
            loading={loading}
            onClick={handleFiltersAction}
            size="large"
          >
            {filtersActionTitle}
          </Button>
        </div>
      </div>
    </aside>
  );
};

const MemoizedFilters = memo(Filters, deepCompare);

export { MemoizedFilters as Filters };
